import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Flag, CheckCircle2, MonitorDot, ArrowLeft } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { Md } from "@/components/academy/Md";
import { SlaBadge } from "@/components/mod/SlaBadge";
import { OpsTicket, simComplete } from "@/components/academy/OpsTicket";
import { OpsEvaluation } from "@/components/academy/OpsEvaluation";

function Queue({ tickets, drafts, sel, setSel }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="sim-queue">
      {tickets.map((t) => {
        const done = simComplete(drafts[t.id]);
        return (
          <button key={t.id} data-testid={`sim-queue-${t.id}`} onClick={() => setSel(t.id)}
            className={`relative block w-full border-b border-zinc-800/50 px-4 py-3 text-left transition-colors ${sel === t.id ? "bg-zinc-800/40" : "hover:bg-zinc-900/60"}`}>
            {sel === t.id && <span className="absolute bottom-0 left-0 top-0 w-0.5 bg-blue-500" />}
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] text-zinc-200">{t.reference}</span>
              <span className={`rounded border px-1.5 py-0.5 text-[10px] ${done ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-blue-500/40 bg-blue-500/10 text-blue-300"}`}>{done ? "Triaged" : "Received"}</span>
            </div>
            <div className="mt-1 truncate text-[12px] text-zinc-400">{t.platform} · {t.category}</div>
            <div className="mt-1.5"><SlaBadge createdAt={t.created_at} status="received" lang="en" hours={24} closed={false} /></div>
          </button>
        );
      })}
    </div>
  );
}

export function OpsSimulation({ mod, reload, onBack }) {
  const sim = mod.sim;
  const [drafts, setDrafts] = useState(sim.drafts || {});
  const [sel, setSel] = useState(sim.tickets[0]?.id);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const doneCount = sim.tickets.filter((t) => simComplete(drafts[t.id])).length;
  const ticket = sim.tickets.find((t) => t.id === sel);

  const onSaved = (id, d, complete) => {
    setDrafts((x) => ({ ...x, [id]: d }));
    toast.success(complete ? "Triage saved" : "Draft saved, some fields are still missing");
    if (complete) {
      const next = sim.tickets.find((t) => t.id !== id && !simComplete(t.id === id ? d : drafts[t.id]));
      if (next) setSel(next.id);
    }
  };
  const submit = async () => {
    if (!window.confirm("Finish the shift? Your decisions will be evaluated.")) return;
    setBusy(true);
    try { await academy.post("/sim/submit"); await reload(); } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { await academy.post("/sim/reset"); setDrafts({}); setSel(sim.tickets[0]?.id); await reload(); } finally { setBusy(false); }
  };

  if (sim.result) return <OpsEvaluation res={sim.result} tickets={sim.tickets} onRetry={retry} onBack={onBack} />;

  return (
    <div className="space-y-4" data-testid="sim-console">
      <div className="rounded-xl border border-zinc-800/70 bg-[#0b1430] p-5"><Md>{mod.intro}</Md></div>
      <div className="overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
        <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800/70 bg-zinc-950 px-4 py-2.5">
          <MonitorDot size={16} className="text-blue-400" />
          <span className="text-[13px] font-semibold text-zinc-100">Ops Console</span>
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-300">Training environment</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-zinc-400" data-testid="sim-progress"><CheckCircle2 size={13} /> {doneCount}/{sim.tickets.length} triaged</span>
          <button data-testid="sim-submit" onClick={submit} disabled={busy || doneCount < sim.tickets.length}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FFCC00] px-3 py-1.5 text-[12px] font-semibold text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Flag size={13} />} Finish shift
          </button>
        </div>
        <div className="flex h-[calc(100vh-12rem)] min-h-[520px]">
          <div className={`${mobileDetail ? "hidden" : "flex"} w-full min-w-0 flex-col border-r border-zinc-800/70 lg:flex lg:w-[340px] lg:shrink-0`} onClick={() => setMobileDetail(true)}>
            <Queue tickets={sim.tickets} drafts={drafts} sel={sel} setSel={setSel} />
          </div>
          <div className={`${mobileDetail ? "block" : "hidden"} min-w-0 flex-1 overflow-y-auto lg:block`}>
            <button data-testid="sim-mobile-back" onClick={() => setMobileDetail(false)} className="m-3 inline-flex items-center gap-1 text-[12px] text-zinc-400 lg:hidden"><ArrowLeft size={13} /> Queue</button>
            {ticket && <OpsTicket key={ticket.id} t={ticket} draft={drafts[ticket.id]} decisions={sim.decisions} onSaved={onSaved} />}
          </div>
        </div>
      </div>
    </div>
  );
}
